import { isPlatformBrowser } from '@angular/common';
import { Injectable, PLATFORM_ID, inject } from '@angular/core';

import { environment } from '../../../environments/environment';

type DatadogRum = (typeof import('@datadog/browser-rum'))['datadogRum'];

/**
 * The single boundary around the Datadog browser SDKs.
 *
 * Nothing imports `@datadog/browser-rum` or `@datadog/browser-logs` statically:
 * both are browser-only (they reach for `window` as their module bodies
 * evaluate, which throws during an SSR render pass) and together they are ~100
 * kB that environments without a Datadog target should never download. The
 * dynamic `import()` here is what makes the "Datadog loads lazily" claim true.
 *
 * Callers do not need to wait for the load: errors raised before the SDK lands
 * are buffered and replayed once it does.
 */
@Injectable({
  providedIn: 'root'
})
export class DatadogService {
  private readonly isBrowser = isPlatformBrowser(inject(PLATFORM_ID));
  private rum: DatadogRum | null = null;
  private bufferedErrors: Error[] = [];
  private loaded: Promise<void> | null = null;

  /** False on the server, and in any environment with no client token. */
  get enabled(): boolean {
    return this.isBrowser && environment.datadog.clientToken.length > 0;
  }

  /** Loads and initialises RUM + Logs once. Resolves immediately when disabled. */
  init(): Promise<void> {
    if (!this.enabled) return Promise.resolve();
    this.loaded ??= this.load();
    return this.loaded;
  }

  /** Records an error, buffering it when the SDK has not finished loading. */
  addError(error: Error): void {
    if (!this.enabled) return;
    if (this.rum) {
      this.rum.addError(error);
      return;
    }
    this.bufferedErrors.push(error);
  }

  /** Records a custom action; dropped when the SDK is disabled or still loading. */
  addAction(name: string, context: Record<string, unknown>): void {
    this.rum?.addAction(name, context);
  }

  private async load(): Promise<void> {
    const [{ datadogRum }, { datadogLogs }] = await Promise.all([
      import('@datadog/browser-rum'),
      import('@datadog/browser-logs')
    ]);

    datadogRum.init({
      applicationId: environment.datadog.applicationId,
      clientToken: environment.datadog.clientToken,
      site: environment.datadog.site,
      service: environment.datadog.service,
      env: environment.environmentName,
      sessionSampleRate: 100,
      sessionReplaySampleRate: environment.production ? 20 : 0,
      trackUserInteractions: true,
      trackResources: true,
      trackLongTasks: true,
      defaultPrivacyLevel: 'mask-user-input'
    });

    datadogLogs.init({
      clientToken: environment.datadog.clientToken,
      site: environment.datadog.site,
      service: environment.datadog.service,
      env: environment.environmentName,
      forwardErrorsToLogs: true,
      sessionSampleRate: 100
    });

    this.rum = datadogRum;
    for (const error of this.bufferedErrors) {
      datadogRum.addError(error);
    }
    this.bufferedErrors = [];
  }
}
